import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './InfoPopover.css';

type InfoPopoverProps = {
  label: string;
  children: React.ReactNode;
  className?: string;
};

// How close the panel is allowed to get to the edge of the window, and how far
// it sits from the "i" it explains.
const EDGE_GAP = 12;
const TRIGGER_GAP = 10;
const ARROW_INSET = 16;

type Placement = {
  left: number;
  top: number;
  side: 'above' | 'below';
  arrowLeft: number;
};

/**
 * The panel is rendered into the document body and positioned against the
 * window, not against its parent.
 *
 * It used to be absolutely positioned inside the metric it belongs to, centred
 * on the "i" and 330px wide. For the last metric in a row that pushed half the
 * panel past the right edge of the screen, where it was cut off and unreadable.
 * A width breakpoint hid that on very narrow phones only, so the bug survived at
 * every width in between. Measuring the window and clamping the panel into it
 * fixes every width at once, and portalling to the body means no card that
 * clips or transforms its children can cut the panel off either.
 */
export default function InfoPopover({
  label,
  children,
  className = '',
}: InfoPopoverProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const rootRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);
  const titleId = useId();

  const reposition = useCallback(() => {
    const anchorElement = rootRef.current;
    const contentElement = contentRef.current;
    if (!anchorElement || !contentElement) return;
    const anchor = anchorElement.getBoundingClientRect();
    const panel = contentElement.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth || 0;
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight || 0;
    const anchorCenter = anchor.left + anchor.width / 2;

    const rightmostLeft = Math.max(EDGE_GAP, viewportWidth - panel.width - EDGE_GAP);
    const left = Math.min(Math.max(EDGE_GAP, anchorCenter - panel.width / 2), rightmostLeft);

    const fitsAbove = anchor.top - panel.height - TRIGGER_GAP >= EDGE_GAP;
    const lowestTop = Math.max(EDGE_GAP, viewportHeight - panel.height - EDGE_GAP);
    const top = fitsAbove
      ? anchor.top - panel.height - TRIGGER_GAP
      : Math.min(anchor.bottom + TRIGGER_GAP, lowestTop);

    const arrowLeft = Math.min(
      Math.max(anchorCenter - left, ARROW_INSET),
      Math.max(ARROW_INSET, panel.width - ARROW_INSET),
    );

    setPlacement({ left, top, side: fitsAbove ? 'above' : 'below', arrowLeft });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return undefined;
    }
    reposition();
    // A scroll anywhere in the page moves the "i" the panel is pointing at.
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const closeOutside = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return;
      if (rootRef.current?.contains(event.target)) return;
      if (contentRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    document.addEventListener('pointerdown', closeOutside);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.removeEventListener('pointerdown', closeOutside);
    };
  }, [open]);

  // The panel is no longer a DOM child of the trigger, so moving the pointer
  // onto it would otherwise read as leaving the popover entirely.
  const leaveUnless =
    (keepOpenWithin: React.RefObject<HTMLElement | null>) => (event: React.MouseEvent) => {
      const next = event.relatedTarget;
      if (next instanceof Node && keepOpenWithin.current?.contains(next)) return;
      setOpen(false);
    };

  const panel = open ? (
    <span
      ref={contentRef}
      id={titleId}
      className={`info-popover__content info-popover__content--${placement?.side || 'above'}`}
      role="dialog"
      aria-label={`${label} details`}
      data-placed={placement ? 'true' : 'false'}
      style={{
        left: placement?.left ?? 0,
        top: placement?.top ?? 0,
        // SAFETY: a custom property is a valid CSSProperties entry at runtime;
        // the React types just do not model the index signature.
        ['--info-popover-arrow' as string]: `${placement?.arrowLeft ?? 0}px`,
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={leaveUnless(rootRef)}
    >
      <strong className="info-popover__title">{label}</strong>
      {children}
    </span>
  ) : null;

  return (
    <span
      ref={rootRef}
      className={`info-popover ${open ? 'info-popover--open' : ''} ${className}`.trim()}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={leaveUnless(contentRef)}
    >
      <button
        type="button"
        className="info-popover__trigger"
        aria-label={`Explain ${label}`}
        aria-expanded={open}
        aria-controls={open ? titleId : undefined}
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
      >
        i
      </button>
      {panel ? createPortal(panel, document.body) : null}
    </span>
  );
}
